import { useCallback, useEffect, useState, useRef } from "react";
import { TextField } from "@adobe/react-spectrum";
import { useDebounce } from "@uidotdev/usehooks";
import { useSelector } from "react-redux";
import { RootState } from "../../../../state/store";

interface IFilterSearchTextBoxProps {
  onSearchChange: (params: {}) => void;
  filterParams: any;
  serachedText?: string;
  fpaTypeCategory: string;
}

const FilterSearchTextBox: React.FC<IFilterSearchTextBoxProps> = ({
  onSearchChange,
  filterParams,
  serachedText,
  fpaTypeCategory,
}) => {
  const selectedApp = useSelector(
    (state: RootState) => state.userWebLayout.selectedAppId
  );
  const [filterTerm, setFilterTerm] = useState<string>(serachedText ?? "");
  const debouncedFilterTerm = useDebounce(filterTerm, 1000);
  const filterParamsRef = useRef(filterParams);
  const onSearchChangeRef = useRef(onSearchChange);
  const fpaTypeCategoryRef = useRef(fpaTypeCategory);

  // Keep refs in sync with props
  useEffect(() => {
    filterParamsRef.current = filterParams;
  }, [filterParams]);

  useEffect(() => {
    onSearchChangeRef.current = onSearchChange;
  }, [onSearchChange]);

  useEffect(() => {
    fpaTypeCategoryRef.current = fpaTypeCategory;
  }, [fpaTypeCategory]);

  const handleFiltering = async (e: any) => {
    setFilterTerm(e);
  };

  const searchServiceActivities = useCallback(async () => {
    // Use refs to get latest values without causing dependency array issues
    const currentFilterParams = filterParamsRef.current;
    const currentOnSearchChange = onSearchChangeRef.current;
    const currentFpaTypeCategory = fpaTypeCategoryRef.current;

    // Create a new object to avoid mutating the prop
    const updatedFilterParams = currentFilterParams
      ? JSON.parse(JSON.stringify(currentFilterParams))
      : {
          FILTER: {
            COL: [],
          },
        };

    // Ensure FILTER and FILTER.COL exist before checking
    const filterCols = updatedFilterParams?.FILTER?.COL || [];

    if (
      !filterCols.some((obj: any) => obj.name === "nameSearch") &&
      debouncedFilterTerm === ""
    ) {
      return;
    }

    // Ensure FILTER.COL exists
    if (!updatedFilterParams.FILTER) {
      updatedFilterParams.FILTER = { COL: [] };
    }
    if (!updatedFilterParams.FILTER.COL) {
      updatedFilterParams.FILTER.COL = [];
    }

    // Add fpaTypeCategory if it doesn't exist
    const fpaTypeCategoryExists = updatedFilterParams.FILTER.COL.some(
      (col: any) => col.name === "fpaTypeCategory"
    );
    if (!fpaTypeCategoryExists) {
      updatedFilterParams.FILTER.COL.push({
        name: "fpaTypeCategory",
        type: "any",
        value: currentFpaTypeCategory,
      });
    }

    // Update or add nameSearch filter
    const updatedArray = updatedFilterParams.FILTER.COL.map((obj: any) =>
      obj.name === "nameSearch" ? { ...obj, value: debouncedFilterTerm } : obj
    );

    const hasNameSearch = updatedFilterParams.FILTER.COL.some(
      (obj: any) => obj.name === "nameSearch"
    );

    if (!hasNameSearch && debouncedFilterTerm) {
      updatedArray.push({
        name: "nameSearch",
        type: "contains",
        value: debouncedFilterTerm,
      });
    }

    updatedFilterParams.FILTER.COL = updatedArray.filter(
      (col: any) => col.value !== ""
    );
    currentOnSearchChange(updatedFilterParams);
  }, [debouncedFilterTerm]);

  useEffect(() => {
    (async () => {
      searchServiceActivities();
    })();
  }, [debouncedFilterTerm, searchServiceActivities]);

  useEffect(() => {
    setFilterTerm("");
  }, [selectedApp]);

  return (
    <TextField
      value={filterTerm}
      width={"100%"}
      onChange={handleFiltering}
      aria-label="Search services"
    />
  );
};

export default FilterSearchTextBox;
